import { useCallback } from 'react'

import AuthService from 'modules/auth/service'
import usePostLead from 'modules/lead/hooks/usePostLead'
import ChatService from 'modules/chat/service'
import { useLeadDataContext } from 'modules/lead/context'
import { EMPTY_LEAD_DATA } from 'modules/lead/constants'
import { useTransmissionContext } from 'modules/transmission/context'
import { useAuth } from 'modules/auth/hooks/useAuth'

const useLeadLogin = () => {
  const postLead = usePostLead()
  const { setShowConfirm, setData, setIsLoading, data: {
    email,
    name,
    userPlatformId
  } } = useLeadDataContext()
  const { transmissionData: { code, conversationId }, isPrivate } = useTransmissionContext()

  const { changeToPublicAccess, logout } = useAuth()

  const clearLogin = useCallback(async () => {
    changeToPublicAccess()

    setShowConfirm(false)
    setData(EMPTY_LEAD_DATA)

    if (isPrivate) {
      logout()
    }
  }, [changeToPublicAccess, logout, setShowConfirm, setData, isPrivate])

  const confirmLogin = useCallback(async () => {
    setIsLoading(true)

    const accepted = true

    await postLead({
      schedule: code,
      email,
      name,
      accepted,
      userPlatformId
    })

    setData(prevState => ({
      ...prevState,
      accepted
    }))
    setShowConfirm(false)
    setIsLoading(false)
  }, [code, email, name, userPlatformId, postLead, setData, setIsLoading, setShowConfirm])

  const handleAnonymousFormSubmit = useCallback(async (lead: { name: string, email: string }) => {
    if (conversationId) {
      AuthService.removeAnonymousToken()
      await ChatService.getAnonymousToken(lead)
    }

    const response = await postLead({
      schedule: code,
      email: lead.email,
      name: lead.name
    })

    if (response) {
      setData({
        email: response.email,
        name: response.name,
        schedule: response.schedule
      })
    }
  }, [code, conversationId, postLead, setData])

  return { clearLogin, confirmLogin, handleAnonymousFormSubmit }
}

export default useLeadLogin
